import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, useAction, useApi } from "../../api.ts";
import { fmtDateTime } from "../../format.ts";
import { Badge, Button, Card, cn, Dialog, EmptyState, ErrorText, Field, IconButton, Loading, LoadError, PageHeader, Select, Textarea, Input, useConfirm, useToast } from "../../ui/index.tsx";
import { IMPACTS, ImpactBadge, IncidentStatusBadge, incidentScope, INCIDENT_STATUSES, LEVELS, STATUS_LABEL, UpdateTimeline } from "../status/common.tsx";
import type { Game, Incident, IncidentImpact, IncidentStatus, PublicStatus } from "../../../shared/types.ts";

const IMPACT_OPTIONS: IncidentImpact[] = ["minor", "major", "critical", "maintenance"];
const DECLARE_STATUSES: IncidentStatus[] = ["investigating", "identified", "monitoring", "scheduled"];

type QuickStatus = "identified" | "monitoring" | "resolved";
const QUICK: { status: QuickStatus; label: string; message: string }[] = [
  { status: "identified", label: "Mark identified", message: "The issue has been identified and we're working on a fix." },
  { status: "monitoring", label: "Mark monitoring", message: "A fix has been implemented and we're monitoring the results." },
  { status: "resolved", label: "Mark resolved", message: "This incident has been resolved." },
];

export default function Incidents() {
  const incidents = useApi<Incident[]>("/admin/incidents");
  const status = useApi<PublicStatus>("/status");
  const games = useApi<Game[]>("/games");
  const [declaring, setDeclaring] = useState(false);

  const refresh = async () => { await Promise.all([incidents.reload(), status.reload()]); };

  const list = incidents.data ?? [];
  const active = list.filter((i) => i.status !== "resolved");
  const resolved = list.filter((i) => i.status === "resolved");

  return (
    <>
      <PageHeader title="Status & incidents" kicker="// admin / status"
        description="What the public status page shows, and the incidents behind it."
        actions={<Button variant="primary" icon="plus" onClick={() => setDeclaring(true)}>Declare incident</Button>} />

      <div className="space-y-6">
        <StatusPreview status={status.data} error={status.error} retry={status.reload} />

        {incidents.loading && !incidents.data ? <Loading /> : incidents.error && !incidents.data ? <LoadError error={incidents.error} retry={incidents.reload} /> : (
          <>
            <Section title="Active" count={active.length}>
              {active.length === 0
                ? <Card><EmptyState icon="check" title="No active incidents" body="Everything is running normally." /></Card>
                : active.map((i) => <IncidentCard key={i.id} incident={i} onChanged={refresh} />)}
            </Section>
            <Section title="Resolved" count={resolved.length}>
              {resolved.length === 0
                ? <p className="text-sm text-faint">Nothing resolved yet.</p>
                : resolved.map((i) => <IncidentCard key={i.id} incident={i} onChanged={refresh} />)}
            </Section>
          </>
        )}
      </div>

      <DeclareDialog open={declaring} games={games.data ?? []} onClose={() => setDeclaring(false)} onDone={refresh} />
    </>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold tracking-wide">
        {title}<Badge>{count}</Badge>
      </h2>
      {children}
    </section>
  );
}

// ── Live preview ──

function StatusPreview({ status, error, retry }: { status: PublicStatus | undefined; error: { message: string } | null; retry: () => void }) {
  return (
    <Card hud title="Current public status"
      description="Game status and detection changes (Admin / Games) are recorded automatically and feed the uptime bars. Incidents add context and colour the bars for their duration."
      actions={<Link to="/status" target="_blank" className="text-xs text-accent hover:underline">View page</Link>}>
      {!status ? (error ? <LoadError error={error} retry={retry} /> : <Loading />) : (
        <div className="space-y-4">
          <div className={cn("flex items-center gap-3 rounded-lg border px-4 py-3", LEVELS[status.overall].panel)}>
            <span className={cn("size-2.5 animate-pulse-soft rounded-full", LEVELS[status.overall].bg)} />
            <span className={cn("font-display text-lg font-semibold", LEVELS[status.overall].text)}>{LEVELS[status.overall].banner}</span>
          </div>
          <ul className="flex flex-wrap gap-2">
            {status.games.map((g) => {
              const meta = g.level ? LEVELS[g.level] : null;
              return (
                <li key={g.id} className="flex items-center gap-2 rounded-lg border border-line bg-raised/60 px-2.5 py-1.5 text-xs">
                  <span className={cn("size-2 rounded-full", meta ? meta.bg : "bg-faint")} />
                  <span className="font-medium">{g.name}</span>
                  <span className={meta ? meta.text : "text-faint"}>{meta ? meta.label : "In development"}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── Incident card ──

function IncidentCard({ incident: i, onChanged }: { incident: Incident; onChanged: () => Promise<void> }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [formOpen, setFormOpen] = useState(i.status !== "resolved");
  const remove = useAction(async () => { await api.del(`/admin/incidents/${i.id}`); return true; });

  const onDelete = async () => {
    if (!await confirm({ title: "Delete incident?", body: `"${i.title}" and its updates will be removed from the status page.`, confirmLabel: "Delete", danger: true })) return;
    if (await remove.run()) { toast("Incident deleted", "success"); await onChanged(); }
    else toast("Could not delete the incident", "error");
  };

  return (
    <Card className={cn("border-l-2", IMPACTS[i.impact].border)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{i.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ImpactBadge impact={i.impact} />
            <IncidentStatusBadge status={i.status} />
            <span className="text-xs text-muted">{incidentScope(i)}</span>
          </div>
          <p className="mt-2 font-mono text-[11px] text-faint">
            Created {fmtDateTime(i.created_at)}{i.resolved_at && <> · Resolved {fmtDateTime(i.resolved_at)}</>}
          </p>
        </div>
        <IconButton icon="trash" label="Delete incident" onClick={onDelete} className="hover:text-bad" />
      </div>

      <div className="mt-5"><UpdateTimeline updates={i.updates} /></div>

      <div className="mt-5 border-t border-line pt-4">
        {formOpen
          ? <UpdateForm incident={i} onChanged={onChanged} />
          : <Button size="sm" icon="edit" onClick={() => setFormOpen(true)}>Post update or reopen</Button>}
      </div>
    </Card>
  );
}

function UpdateForm({ incident: i, onChanged }: { incident: Incident; onChanged: () => Promise<void> }) {
  const toast = useToast();
  const [status, setStatus] = useState<IncidentStatus>(i.status === "resolved" ? "investigating" : i.status);
  const [message, setMessage] = useState("");
  const post = useAction(async (s: IncidentStatus, m: string) => { await api.post(`/admin/incidents/${i.id}/updates`, { status: s, message: m }); return true; });

  const submit = async (s: IncidentStatus, m: string) => {
    if (!m.trim()) return;
    if (await post.run(s, m.trim())) {
      setMessage("");
      toast(s === "resolved" ? "Incident resolved" : "Update posted", "success");
      await onChanged();
    }
  };

  const onSubmit = (e: FormEvent) => { e.preventDefault(); void submit(status, message); };

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-[11rem_1fr]">
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as IncidentStatus)}>
            {INCIDENT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </Select>
        </Field>
        <Field label="Update message">
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="min-h-16" placeholder="What's happening now?" />
        </Field>
      </div>
      <ErrorText>{post.error}</ErrorText>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" size="sm" loading={post.pending} disabled={!message.trim()}>Post update</Button>
        <span className="mx-1 hidden h-4 w-px bg-line sm:block" />
        {QUICK.filter((q) => q.status !== i.status).map((q) => (
          <Button key={q.status} size="sm" disabled={post.pending} onClick={() => void submit(q.status, message.trim() || q.message)}>{q.label}</Button>
        ))}
      </div>
      <p className="text-xs text-faint">The quick buttons use your message if you've typed one, otherwise a standard line.</p>
    </form>
  );
}

// ── Declare dialog ──

function DeclareDialog({ open, games, onClose, onDone }: { open: boolean; games: Game[]; onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useToast();
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [gameId, setGameId] = useState("");
  const [impact, setImpact] = useState<IncidentImpact>("minor");
  const [status, setStatus] = useState<IncidentStatus>("investigating");
  const [message, setMessage] = useState("");

  const create = useAction(async () => {
    await api.post("/admin/incidents", { title: title.trim(), message: message.trim(), impact, status, ...(gameId ? { game_id: gameId } : {}) });
    return true;
  });
  const { setError } = create;

  useEffect(() => {
    if (!open) return;
    setTitle(""); setGameId(""); setImpact("minor"); setStatus("investigating"); setMessage(""); setError(null);
    const t = setTimeout(() => titleRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, setError]);

  const onImpact = (v: IncidentImpact) => {
    setImpact(v);
    if (v === "maintenance" && status === "investigating") setStatus("scheduled");
    else if (v !== "maintenance" && status === "scheduled") setStatus("investigating");
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (await create.run()) {
      toast("Incident declared", "success");
      onClose();
      await onDone();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Declare incident" description="This appears on the public status page straight away."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" form="declare-incident" variant="primary" loading={create.pending} disabled={!title.trim() || !message.trim()}>Declare</Button>
      </>}>
      <form id="declare-incident" onSubmit={onSubmit} className="space-y-4">
        <Field label="Title"><Input ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="e.g. Login failures on the loader" /></Field>
        <Field label="Affected game">
          <Select value={gameId} onChange={(e) => setGameId(e.target.value)}>
            <option value="">All games</option>
            {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Impact">
            <Select value={impact} onChange={(e) => onImpact(e.target.value as IncidentImpact)}>
              {IMPACT_OPTIONS.map((v) => <option key={v} value={v}>{IMPACTS[v].label}</option>)}
            </Select>
          </Field>
          <Field label="Initial status">
            <Select value={status} onChange={(e) => setStatus(e.target.value as IncidentStatus)}>
              {DECLARE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Message"><Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What do you know so far?" /></Field>
        <ErrorText>{create.error}</ErrorText>
      </form>
    </Dialog>
  );
}
